import { Canvas } from '@react-three/fiber';
import { Html, OrbitControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import * as THREE from 'three';
import { stableHashUnit, type PositionedGraph, type PositionedNode } from '../lib/graph-layout';
import type { VaultLink } from '../../shared/types';
import type { CameraFlight, NavigationMode } from '../App';

interface KosmosSceneProps {
  graph: PositionedGraph;
  selectedNodeId?: string;
  hoveredNodeId?: string;
  liveNodeIds: Set<string>;
  primaryLiveNodeId?: string;
  navigationMode: NavigationMode;
  cameraFlight: CameraFlight;
  emergingNodeIds: Set<string>;
  labelsEnabled: boolean;
  onSelectNode: (nodeId: string) => void;
  onHoverNode: (nodeId?: string) => void;
}

export function KosmosScene({
  graph,
  selectedNodeId,
  hoveredNodeId,
  liveNodeIds,
  primaryLiveNodeId,
  navigationMode,
  cameraFlight,
  emergingNodeIds,
  labelsEnabled,
  onSelectNode,
  onHoverNode
}: KosmosSceneProps) {
  const controlsRef = useRef<any>(null);
  const cameraFocusNode = selectedNodeId
    ? graph.nodeById.get(selectedNodeId)
    : primaryLiveNodeId
      ? graph.nodeById.get(primaryLiveNodeId)
      : undefined;
  const fog = fogForMode(navigationMode);

  return (
    <Canvas
      className="kosmos-canvas"
      camera={{ position: [0, 42, 124], fov: 58 }}
      dpr={[1, 1.5]}
      gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
      onCreated={({ gl }) => {
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1.08;
      }}
    >
      <color attach="background" args={['#030711']} />
      <fog attach="fog" args={[fog.color, fog.near, fog.far]} />
      <ambientLight intensity={0.22} />
      <hemisphereLight args={['#9bdcff', '#090b16', 0.36]} />
      <directionalLight position={[34, 58, 52]} intensity={1.35} color="#dff7ff" />
      <pointLight position={[42, 36, 24]} intensity={1.65} color="#7dd3fc" />
      <pointLight position={[-54, 28, -30]} intensity={1.05} color="#ff8bd1" />
      <pointLight position={[0, -18, 52]} intensity={0.9} color="#f4d35e" />
      <CosmosBackdrop />
      <CameraFlightRig
        cameraFlight={cameraFlight}
        focusNode={cameraFocusNode}
        controlsRef={controlsRef}
      />
      <CameraLensRig mode={navigationMode} />
      <FlyNavigator active={navigationMode === 'fly'} controlsRef={controlsRef} />
      <group>
        <DepthAtmosphere mode={navigationMode} focusNode={cameraFocusNode} />
        <DepthHalo active={navigationMode === 'deep'} />
        <DepthCorridor mode={navigationMode} />
        <PresentationRouteLayer nodes={graph.nodes} active={navigationMode !== 'overview'} />
        <ClusterMarkers nodes={graph.nodes} labelsEnabled={labelsEnabled} />
        <PrimaryFocusAura node={primaryLiveNodeId ? graph.nodeById.get(primaryLiveNodeId) : undefined} />
        <GraphLinks
          links={graph.links}
          nodeById={graph.nodeById}
          selectedNodeId={selectedNodeId}
          hoveredNodeId={hoveredNodeId}
          liveNodeIds={liveNodeIds}
          navigationMode={navigationMode}
        />
        <NodeField
          nodes={graph.nodes}
          liveNodeIds={liveNodeIds}
          emergingNodeIds={emergingNodeIds}
          primaryLiveNodeId={primaryLiveNodeId}
          selectedNodeId={selectedNodeId}
          hoveredNodeId={hoveredNodeId}
          labelsEnabled={labelsEnabled}
          navigationMode={navigationMode}
          onSelectNode={onSelectNode}
          onHoverNode={onHoverNode}
        />
      </group>
      <OrbitControls
        ref={controlsRef}
        makeDefault
        enableDamping
        dampingFactor={0.055}
        autoRotate={!selectedNodeId}
        autoRotateSpeed={0.14}
        enablePan
        zoomSpeed={0.82}
        rotateSpeed={0.58}
        minDistance={18}
        maxDistance={240}
        enabled={navigationMode !== 'fly'}
        target={[0, 6, 0]}
      />
    </Canvas>
  );
}

function CosmosBackdrop() {
  return (
    <group>
      <StaticStarfield count={760} radius={178} color="#c7f4ff" opacity={0.34} size={0.38} seed="far" />
      <StaticStarfield count={340} radius={112} color="#f4d35e" opacity={0.16} size={0.26} seed="near" />
      <mesh position={[0, -28, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[34, 118, 160]} />
        <meshBasicMaterial color="#7dd3fc" transparent opacity={0.018} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 38, -72]} rotation={[Math.PI / 2.4, 0, -0.22]}>
        <torusGeometry args={[72, 0.035, 8, 180]} />
        <meshBasicMaterial
          color="#f4d35e"
          transparent
          opacity={0.052}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  );
}

function StaticStarfield({
  count,
  radius,
  color,
  opacity,
  size,
  seed
}: {
  count: number;
  radius: number;
  color: string;
  opacity: number;
  size: number;
  seed: string;
}) {
  const geometry = useMemo(() => {
    const positions = new Float32Array(count * 3);
    for (let index = 0; index < count; index += 1) {
      const a = stableHashUnit(`${seed}:a:${index}`) * Math.PI * 2;
      const v = stableHashUnit(`${seed}:v:${index}`) * 2 - 1;
      const r = radius * Math.cbrt(0.28 + stableHashUnit(`${seed}:r:${index}`) * 0.72);
      const band = (stableHashUnit(`${seed}:band:${index}`) - 0.5) * 0.22;
      const y = Math.asin(Math.max(-1, Math.min(1, v * 0.76 + band)));
      positions[index * 3] = Math.cos(a) * Math.cos(y) * r;
      positions[index * 3 + 1] = Math.sin(y) * r * 0.58;
      positions[index * 3 + 2] = Math.sin(a) * Math.cos(y) * r;
    }
    const points = new THREE.BufferGeometry();
    points.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return points;
  }, [count, radius, seed]);

  return (
    <points geometry={geometry}>
      <pointsMaterial color={color} size={size} transparent opacity={opacity} depthWrite={false} sizeAttenuation />
    </points>
  );
}

function fogForMode(mode: NavigationMode): { color: string; near: number; far: number } {
  if (mode === 'fly') return { color: '#030711', near: 34, far: 158 };
  if (mode === 'deep') return { color: '#030711', near: 42, far: 172 };
  if (mode === 'focus') return { color: '#030711', near: 62, far: 196 };
  return { color: '#030711', near: 86, far: 238 };
}

function CameraLensRig({ mode }: { mode: NavigationMode }) {
  const { camera } = useThree();

  useFrame((_, delta) => {
    if (!(camera instanceof THREE.PerspectiveCamera)) return;
    const targetFov = mode === 'fly' ? 64 : mode === 'deep' ? 50 : mode === 'focus' ? 54 : 58;
    const nextFov = THREE.MathUtils.lerp(camera.fov, targetFov, 1 - Math.exp(-delta * 2.4));
    if (Math.abs(nextFov - camera.fov) < 0.01) return;
    camera.fov = nextFov;
    camera.updateProjectionMatrix();
  });

  return null;
}

function DepthAtmosphere({ mode, focusNode }: { mode: NavigationMode; focusNode?: PositionedNode }) {
  const groupRef = useRef<THREE.Group>(null);
  const { camera } = useThree();
  const activeDepth = mode === 'deep' || mode === 'fly';

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const elapsed = clock.getElapsedTime();
    groupRef.current.rotation.y = elapsed * (activeDepth ? 0.012 : 0.006);
    groupRef.current.rotation.z = Math.sin(elapsed * 0.08) * 0.018;
    groupRef.current.position.x = camera.position.x * 0.018;
    groupRef.current.position.z = camera.position.z * 0.012;
  });

  const focus = focusNode ? new THREE.Vector3(...focusNode.position) : undefined;

  return (
    <group ref={groupRef}>
      {[0, 1, 2].map((index) => (
        <mesh key={index} position={[0, 3 + index * 4, -22 + index * 34]} scale={[76 + index * 26, 22 + index * 7, 76 + index * 26]}>
          <sphereGeometry args={[1, 36, 18]} />
          <meshBasicMaterial
            color={index === 1 ? '#7dd3fc' : '#94a3b8'}
            transparent
            opacity={(activeDepth ? 0.022 : 0.014) - index * 0.003}
            depthWrite={false}
            side={THREE.BackSide}
            blending={THREE.AdditiveBlending}
          />
        </mesh>
      ))}
      <ParallaxLayer radius={108} y={-20} z={-68} color="#7dd3fc" opacity={mode === 'overview' ? 0.016 : 0.026} tilt={0.36} />
      <ParallaxLayer radius={74} y={18} z={-22} color="#f4d35e" opacity={mode === 'deep' ? 0.034 : 0.019} tilt={-0.22} />
      <ParallaxLayer radius={132} y={34} z={38} color="#a78bfa" opacity={mode === 'fly' ? 0.028 : 0.016} tilt={0.62} />
      {focus && (
        <group position={focus}>
          <mesh rotation={[Math.PI / 2, 0, 0]} scale={[18, 18, 1]}>
            <ringGeometry args={[0.66, 0.68, 128]} />
            <meshBasicMaterial color="#ffffff" transparent opacity={0.18} depthWrite={false} blending={THREE.AdditiveBlending} />
          </mesh>
          <mesh scale={[18, 18, 5]}>
            <sphereGeometry args={[1, 32, 16]} />
            <meshBasicMaterial color="#7dd3fc" transparent opacity={0.028} depthWrite={false} blending={THREE.AdditiveBlending} />
          </mesh>
        </group>
      )}
    </group>
  );
}

function ParallaxLayer({
  radius,
  y,
  z,
  color,
  opacity,
  tilt
}: {
  radius: number;
  y: number;
  z: number;
  color: string;
  opacity: number;
  tilt: number;
}) {
  return (
    <group position={[0, y, z]} rotation={[Math.PI / 2 + tilt, 0, tilt * 0.42]}>
      <mesh>
        <ringGeometry args={[radius * 0.52, radius * 0.525, 192]} />
        <meshBasicMaterial color={color} transparent opacity={opacity * 0.24} depthWrite={false} blending={THREE.AdditiveBlending} />
      </mesh>
      <mesh>
        <torusGeometry args={[radius * 0.34, 0.014, 8, 164]} />
        <meshBasicMaterial color={color} transparent opacity={opacity * 0.58} depthWrite={false} blending={THREE.AdditiveBlending} />
      </mesh>
      <mesh rotation={[0, 0, Math.PI / 3]}>
        <torusGeometry args={[radius * 0.2, 0.01, 8, 132]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={opacity * 0.28} depthWrite={false} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
}

function PresentationRouteLayer({ nodes, active }: { nodes: PositionedNode[]; active: boolean }) {
  const routeGeometry = useMemo(() => {
    const clusterPoints = buildRoutePoints(nodes);
    const geometry = new THREE.BufferGeometry();
    if (clusterPoints.length < 3) {
      geometry.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
      return geometry;
    }
    const curve = new THREE.CatmullRomCurve3(clusterPoints, false, 'centripetal', 0.35);
    const points = curve.getPoints(140);
    const segmentPositions: number[] = [];
    for (let index = 0; index < points.length - 1; index += 1) {
      segmentPositions.push(...points[index].toArray(), ...points[index + 1].toArray());
    }
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(segmentPositions, 3));
    return geometry;
  }, [nodes]);

  useEffect(() => () => routeGeometry.dispose(), [routeGeometry]);

  if (!active) return null;

  return (
    <group>
      <lineSegments geometry={routeGeometry}>
        <lineBasicMaterial color="#f4d35e" transparent opacity={0.16} depthWrite={false} blending={THREE.AdditiveBlending} />
      </lineSegments>
      <lineSegments geometry={routeGeometry}>
        <lineBasicMaterial color="#7dd3fc" transparent opacity={0.055} depthWrite={false} />
      </lineSegments>
    </group>
  );
}

function buildRoutePoints(nodes: PositionedNode[]): THREE.Vector3[] {
  const buckets = new Map<string, { count: number; sum: THREE.Vector3; color: string }>();
  for (const node of nodes) {
    if (node.kind === 'unresolved' || node.area === 'Vault') continue;
    const current = buckets.get(node.area) ?? { count: 0, sum: new THREE.Vector3(), color: node.color };
    current.count += 1;
    current.sum.add(new THREE.Vector3(...node.position));
    buckets.set(node.area, current);
  }

  return [...buckets.entries()]
    .map(([area, bucket]) => ({
      area,
      count: bucket.count,
      point: bucket.sum.multiplyScalar(1 / Math.max(1, bucket.count)).add(new THREE.Vector3(0, 7, 0))
    }))
    .sort((a, b) => b.count - a.count || a.area.localeCompare(b.area, 'de'))
    .slice(0, 8)
    .sort((a, b) => Math.atan2(a.point.z, a.point.x) - Math.atan2(b.point.z, b.point.x))
    .map((entry) => entry.point);
}

function FlyNavigator({ active, controlsRef }: { active: boolean; controlsRef: RefObject<any> }) {
  const { camera, gl } = useThree();
  const keys = useRef(new Set<string>());
  const direction = useRef(new THREE.Vector3());
  const right = useRef(new THREE.Vector3());
  const up = useRef(new THREE.Vector3(0, 1, 0));
  const targetVelocity = useRef(new THREE.Vector3());
  const velocity = useRef(new THREE.Vector3());
  const targetTurn = useRef(new THREE.Vector2());
  const turnVelocity = useRef(new THREE.Vector2());
  const worldDirection = useRef(new THREE.Vector3());
  const yaw = useRef(0);
  const pitch = useRef(0);

  useEffect(() => {
    if (!active) {
      keys.current.clear();
      velocity.current.set(0, 0, 0);
      targetVelocity.current.set(0, 0, 0);
      targetTurn.current.set(0, 0);
      turnVelocity.current.set(0, 0);
      if (document.pointerLockElement === gl.domElement) document.exitPointerLock();
      return;
    }

    gl.domElement.tabIndex = 0;
    gl.domElement.focus();
    camera.getWorldDirection(worldDirection.current);
    pitch.current = Math.asin(THREE.MathUtils.clamp(worldDirection.current.y, -1, 1));
    yaw.current = Math.atan2(-worldDirection.current.x, -worldDirection.current.z);
    camera.rotation.set(pitch.current, yaw.current, 0, 'YXZ');

    const requestPointerLock = () => {
      gl.domElement.focus();
      void gl.domElement.requestPointerLock?.();
    };
    const onMouseMove = (event: MouseEvent) => {
      if (document.pointerLockElement !== gl.domElement) return;
      yaw.current -= event.movementX * 0.00135;
      pitch.current -= event.movementY * 0.00118;
      pitch.current = Math.max(-Math.PI / 2 + 0.08, Math.min(Math.PI / 2 - 0.08, pitch.current));
      camera.rotation.set(pitch.current, yaw.current, 0, 'YXZ');
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (FLIGHT_KEYS.has(event.code)) {
        event.preventDefault();
        keys.current.add(event.code);
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      keys.current.delete(event.code);
    };

    gl.domElement.addEventListener('click', requestPointerLock);
    document.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      gl.domElement.removeEventListener('click', requestPointerLock);
      document.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      keys.current.clear();
      velocity.current.set(0, 0, 0);
      targetVelocity.current.set(0, 0, 0);
      targetTurn.current.set(0, 0);
      turnVelocity.current.set(0, 0);
      if (document.pointerLockElement === gl.domElement) document.exitPointerLock();
    };
  }, [active, camera, gl.domElement]);

  useFrame((_, delta) => {
    if (!active) return;
    const frameDelta = Math.min(delta, 0.05);

    camera.getWorldDirection(direction.current);
    right.current.crossVectors(direction.current, up.current).normalize();

    const sprint = keys.current.has('ShiftLeft') || keys.current.has('ShiftRight');
    const speed = sprint ? 58 : 24;
    targetVelocity.current.set(0, 0, 0);
    if (keys.current.has('KeyW')) targetVelocity.current.addScaledVector(direction.current, speed);
    if (keys.current.has('KeyS')) targetVelocity.current.addScaledVector(direction.current, -speed);
    if (keys.current.has('KeyD')) targetVelocity.current.addScaledVector(right.current, speed);
    if (keys.current.has('KeyA')) targetVelocity.current.addScaledVector(right.current, -speed);
    if (keys.current.has('KeyE') || keys.current.has('Space')) targetVelocity.current.y += speed;
    if (keys.current.has('ControlLeft') || keys.current.has('ControlRight') || keys.current.has('KeyC')) {
      targetVelocity.current.y -= speed;
    }
    velocity.current.lerp(targetVelocity.current, 1 - Math.exp(-frameDelta * 9.4));
    camera.position.addScaledVector(velocity.current, frameDelta);

    const turnSpeed = sprint ? 1.42 : 0.92;
    targetTurn.current.set(0, 0);
    if (keys.current.has('ArrowLeft')) targetTurn.current.x += turnSpeed;
    if (keys.current.has('ArrowRight')) targetTurn.current.x -= turnSpeed;
    if (keys.current.has('ArrowUp')) targetTurn.current.y += turnSpeed * 0.66;
    if (keys.current.has('ArrowDown')) targetTurn.current.y -= turnSpeed * 0.66;
    turnVelocity.current.lerp(targetTurn.current, 1 - Math.exp(-frameDelta * 8.2));
    if (turnVelocity.current.lengthSq() > 0.000001) {
      yaw.current += turnVelocity.current.x * frameDelta;
      pitch.current += turnVelocity.current.y * frameDelta;
      pitch.current = Math.max(-Math.PI / 2 + 0.08, Math.min(Math.PI / 2 - 0.08, pitch.current));
      camera.rotation.set(pitch.current, yaw.current, 0, 'YXZ');
    }

    if (controlsRef.current) {
      controlsRef.current.target.copy(camera.position.clone().add(direction.current.clone().multiplyScalar(28)));
    }
  });

  return null;
}

const FLIGHT_KEYS = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'KeyE',
  'Space',
  'ShiftLeft',
  'ShiftRight',
  'ControlLeft',
  'ControlRight',
  'KeyC',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown'
]);

function PrimaryFocusAura({ node }: { node?: PositionedNode }) {
  if (!node) return null;

  return (
    <group position={node.position}>
      <mesh rotation={[Math.PI / 2, 0, 0]} scale={8.5}>
        <torusGeometry args={[1, 0.025, 8, 96]} />
        <meshBasicMaterial color="#f4d35e" transparent opacity={0.72} depthWrite={false} />
      </mesh>
      <mesh rotation={[Math.PI / 2.6, 0.2, 0.6]} scale={12.5}>
        <torusGeometry args={[1, 0.014, 8, 128]} />
        <meshBasicMaterial
          color="#7dd3fc"
          transparent
          opacity={0.22}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
      <mesh scale={[11, 11, 2.6]}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial
          color="#f4d35e"
          transparent
          opacity={0.032}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  );
}

function CameraFlightRig({
  cameraFlight,
  focusNode,
  controlsRef
}: {
  cameraFlight: CameraFlight;
  focusNode?: PositionedNode;
  controlsRef: RefObject<any>;
}) {
  const { camera, size } = useThree();
  const lookAtTarget = useRef(new THREE.Vector3(0, 6, 0));
  const activeFlightId = useRef(-1);
  const flightProgress = useRef(1);
  const startPosition = useRef(new THREE.Vector3());
  const startLookAt = useRef(new THREE.Vector3());
  const targetPosition = useRef(new THREE.Vector3());
  const targetLookAt = useRef(new THREE.Vector3());

  useFrame((_, delta) => {
    const focusPosition = focusNode ? new THREE.Vector3(...focusNode.position) : new THREE.Vector3(0, 6, 0);
    const compactViewport = size.width < 720;
    const nextTargetPosition = cameraPositionForMode(cameraFlight.mode, focusPosition, compactViewport);
    const nextTargetLookAt =
      cameraFlight.mode === 'overview'
        ? new THREE.Vector3(0, compactViewport ? 17 : 6, compactViewport ? 18 : 0)
        : focusPosition.clone();

    if (activeFlightId.current !== cameraFlight.id) {
      activeFlightId.current = cameraFlight.id;
      flightProgress.current = 0;
      startPosition.current.copy(camera.position);
      startLookAt.current.copy(controlsRef.current?.target ?? lookAtTarget.current);
      targetPosition.current.copy(nextTargetPosition);
      targetLookAt.current.copy(nextTargetLookAt);
    }

    if (flightProgress.current >= 1) return;

    const duration = cameraFlight.mode === 'overview' ? 1.65 : cameraFlight.mode === 'focus' ? 1.32 : 1.05;
    flightProgress.current = Math.min(1, flightProgress.current + delta / duration);
    const eased = smootherStep(flightProgress.current);
    const arc = Math.sin(Math.PI * eased);
    const travel = startPosition.current.distanceTo(targetPosition.current);
    const arcHeight = Math.min(cameraFlight.mode === 'overview' ? 18 : 9, travel * 0.16);
    const lateral = Math.min(10, travel * 0.08) * (stableHashUnit(`${focusNode?.id ?? 'overview'}:${cameraFlight.mode}`) > 0.5 ? 1 : -1);
    const pathDirection = targetPosition.current.clone().sub(startPosition.current).normalize();
    const side = new THREE.Vector3().crossVectors(pathDirection, new THREE.Vector3(0, 1, 0)).normalize();
    const basePosition = startPosition.current.clone().lerp(targetPosition.current, eased);
    basePosition.y += arc * arcHeight;
    basePosition.addScaledVector(side, arc * lateral);
    camera.position.copy(basePosition);
    lookAtTarget.current.copy(startLookAt.current.clone().lerp(targetLookAt.current, smootherStep(Math.min(1, eased + 0.12))));
    camera.lookAt(lookAtTarget.current);
    if (controlsRef.current) {
      controlsRef.current.target.copy(lookAtTarget.current);
      controlsRef.current.update();
    }

    if (flightProgress.current >= 1) {
      camera.position.copy(targetPosition.current);
      lookAtTarget.current.copy(targetLookAt.current);
      camera.lookAt(lookAtTarget.current);
    }
  });

  return null;
}

function smootherStep(value: number): number {
  const x = THREE.MathUtils.clamp(value, 0, 1);
  return x * x * x * (x * (x * 6 - 15) + 10);
}

function cameraPositionForMode(
  mode: NavigationMode,
  focusPosition: THREE.Vector3,
  compactViewport: boolean
): THREE.Vector3 {
  if (mode === 'focus') {
    return focusPosition.clone().add(compactViewport ? new THREE.Vector3(10, 11, 32) : new THREE.Vector3(16, 13, 42));
  }

  if (mode === 'deep') {
    return focusPosition.clone().add(compactViewport ? new THREE.Vector3(5, 6, 15) : new THREE.Vector3(7, 7, 19));
  }

  if (compactViewport) {
    return new THREE.Vector3(0, 30, 94);
  }

  return new THREE.Vector3(0, 46, 134);
}

function DepthHalo({ active }: { active: boolean }) {
  if (!active) return null;

  const baseOpacity = active ? 0.064 : 0.009;

  return (
    <group position={[0, 2, -8]}>
      {[18, 34, 52, 72].map((radius, index) => (
        <mesh key={radius} rotation={[Math.PI / 2, 0, index * 0.35]}>
          <torusGeometry args={[radius, 0.014, 8, 132]} />
          <meshBasicMaterial
            color={index % 2 === 0 ? '#7dd3fc' : '#f4d35e'}
            transparent
            opacity={Math.max(0.002, baseOpacity - index * (active ? 0.006 : 0.0014))}
            depthWrite={false}
            blending={THREE.AdditiveBlending}
          />
        </mesh>
      ))}
    </group>
  );
}

function DepthCorridor({ mode }: { mode: NavigationMode }) {
  const opacity = mode === 'deep' ? 0.065 : mode === 'focus' ? 0.028 : mode === 'fly' ? 0.01 : 0.012;
  const length = mode === 'deep' ? 7 : mode === 'fly' ? 6 : 5;

  return (
    <group position={[0, 2, 8]}>
      {Array.from({ length }).map((_, index) => {
        const z = -58 + index * 22;
        const radius = 20 + index * 8;
        return (
          <group key={z} position={[0, index * 1.6, z]}>
            <mesh rotation={[Math.PI / 2, 0, index * 0.28]}>
              <torusGeometry args={[radius, 0.018, 8, 144]} />
              <meshBasicMaterial
                color={index % 2 === 0 ? '#7dd3fc' : '#f4d35e'}
                transparent
                opacity={opacity}
                depthWrite={false}
                blending={THREE.AdditiveBlending}
              />
            </mesh>
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <ringGeometry args={[radius * 0.62, radius * 0.625, 96]} />
              <meshBasicMaterial
                color="#94a3b8"
                transparent
                opacity={opacity * 0.18}
                depthWrite={false}
              />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

function ClusterMarkers({ nodes, labelsEnabled }: { nodes: PositionedNode[]; labelsEnabled: boolean }) {
  const clusters = useMemo(() => {
    const buckets = new Map<string, { count: number; color: string; sum: [number, number, number] }>();

    for (const node of nodes) {
      if (node.kind === 'unresolved' || node.area === 'Vault') continue;
      const current = buckets.get(node.area) ?? {
        count: 0,
        color: node.color,
        sum: [0, 0, 0] as [number, number, number]
      };
      current.count += 1;
      current.sum[0] += node.position[0];
      current.sum[1] += node.position[1];
      current.sum[2] += node.position[2];
      buckets.set(node.area, current);
    }

    return [...buckets.entries()]
      .map(([area, bucket]) => ({
        area,
        color: bucket.color,
        count: bucket.count,
        position: [
          bucket.sum[0] / bucket.count,
          bucket.sum[1] / bucket.count + 10,
          bucket.sum[2] / bucket.count
        ] as [number, number, number],
        scale: Math.min(18, 6 + Math.sqrt(bucket.count) * 0.5)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [nodes]);

  return (
    <>
      {clusters.map((cluster) => (
        <group key={cluster.area} position={cluster.position}>
          <mesh scale={cluster.scale * 1.15}>
            <sphereGeometry args={[1, 32, 20]} />
            <meshBasicMaterial
              color={cluster.color}
              transparent
              opacity={0.018}
              depthWrite={false}
              blending={THREE.AdditiveBlending}
            />
          </mesh>
          <mesh rotation={[Math.PI / 2, 0, stableHashUnit(cluster.area) * Math.PI]}>
            <torusGeometry args={[cluster.scale, 0.025, 8, 80]} />
            <meshBasicMaterial
              color={cluster.color}
              transparent
              opacity={0.16}
              depthWrite={false}
              blending={THREE.AdditiveBlending}
            />
          </mesh>
          {labelsEnabled && (
            <Html distanceFactor={24} position={[0, cluster.scale * 0.14 + 2.8, 0]} center zIndexRange={[3, 0]}>
              <div
                className="cluster-label"
                style={{
                  borderColor: `${cluster.color}99`,
                  color: cluster.color,
                  boxShadow: `0 0 26px ${cluster.color}44`
                }}
              >
              <strong>{cluster.area}</strong>
              <span>{cluster.count}</span>
            </div>
            </Html>
          )}
        </group>
      ))}
    </>
  );
}

interface GraphLinksProps {
  links: VaultLink[];
  nodeById: Map<string, PositionedNode>;
  selectedNodeId?: string;
  hoveredNodeId?: string;
  liveNodeIds: Set<string>;
  navigationMode: NavigationMode;
}

function GraphLinks({ links, nodeById, selectedNodeId, hoveredNodeId, liveNodeIds, navigationMode }: GraphLinksProps) {
  const focusNodeIds = useMemo(
    () => new Set([selectedNodeId, hoveredNodeId, ...liveNodeIds].filter(Boolean) as string[]),
    [hoveredNodeId, liveNodeIds, selectedNodeId]
  );
  const linkStyle = linkStyleForMode(navigationMode, focusNodeIds.size > 0);

  const ambientGeometry = useMemo(() => {
    const positions: number[] = [];
    const colors: number[] = [];
    const color = new THREE.Color();

    for (const link of links) {
      if (link.kind === 'contains') continue;
      const source = nodeById.get(link.source);
      const target = nodeById.get(link.target);
      if (!source || !target) continue;
      if (focusNodeIds.has(link.source) || focusNodeIds.has(link.target)) continue;
      if (!shouldShowAmbientLink(link, navigationMode)) continue;

      positions.push(...source.position, ...target.position);
      const opacityColor = link.kind === 'property' ? '#f4d35e' : link.kind === 'markdown' ? '#7dd3fc' : '#94a3b8';
      color.set(opacityColor);
      colors.push(color.r, color.g, color.b, color.r, color.g, color.b);
    }

    const lineGeometry = new THREE.BufferGeometry();
    lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    lineGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    return lineGeometry;
  }, [focusNodeIds, links, navigationMode, nodeById]);

  const focusGeometry = useMemo(() => {
    const positions: number[] = [];
    const colors: number[] = [];
    const color = new THREE.Color();

    for (const link of links) {
      if (link.kind === 'contains') continue;
      if (!focusNodeIds.has(link.source) && !focusNodeIds.has(link.target)) continue;
      if (!shouldShowFocusLink(link, selectedNodeId, hoveredNodeId, navigationMode)) continue;
      const source = nodeById.get(link.source);
      const target = nodeById.get(link.target);
      if (!source || !target) continue;

      positions.push(...source.position, ...target.position);
      color.set(link.kind === 'property' ? '#f4d35e' : '#7dd3fc');
      colors.push(color.r, color.g, color.b, color.r, color.g, color.b);
    }

    const lineGeometry = new THREE.BufferGeometry();
    lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    lineGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    return lineGeometry;
  }, [focusNodeIds, hoveredNodeId, links, navigationMode, nodeById, selectedNodeId]);

  return (
    <>
      <lineSegments geometry={ambientGeometry}>
        <lineBasicMaterial vertexColors transparent opacity={linkStyle.ambientOpacity} depthWrite={false} />
      </lineSegments>
      <lineSegments geometry={focusGeometry}>
        <lineBasicMaterial
          vertexColors
          transparent
          opacity={linkStyle.focusOpacity}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </lineSegments>
    </>
  );
}

function linkStyleForMode(mode: NavigationMode, hasFocus: boolean): { ambientOpacity: number; focusOpacity: number } {
  if (mode === 'fly') return { ambientOpacity: hasFocus ? 0.014 : 0.02, focusOpacity: 0.5 };
  if (mode === 'deep') return { ambientOpacity: hasFocus ? 0.016 : 0.022, focusOpacity: 0.62 };
  if (mode === 'focus') return { ambientOpacity: hasFocus ? 0.018 : 0.026, focusOpacity: 0.6 };
  return { ambientOpacity: hasFocus ? 0.02 : 0.028, focusOpacity: 0.54 };
}

interface NodeSphereProps {
  node: PositionedNode;
  selected: boolean;
  hovered: boolean;
  live: boolean;
  emerging: boolean;
  primaryLive: boolean;
  showLabel: boolean;
  labelMode: 'near' | 'focus';
  onSelect: (nodeId: string) => void;
  onHover: (nodeId?: string) => void;
}

function NodeSphere({
  node,
  selected,
  hovered,
  live,
  emerging,
  primaryLive,
  showLabel,
  labelMode,
  onSelect,
  onHover
}: NodeSphereProps) {
  const [pulseOffset] = useState(() => Math.random() * Math.PI * 2);
  const color = node.kind === 'unresolved' ? '#ffb86b' : node.color;
  const emissive = live || selected || hovered || emerging ? color : '#0b1020';
  const scale = node.radius * (primaryLive ? 2.45 : live ? 1.78 : emerging ? 1.55 : selected ? 1.58 : hovered ? 1.34 : 0.92);

  return (
    <group position={node.position}>
      <mesh
        scale={scale}
        onClick={(event) => {
          event.stopPropagation();
          onSelect(node.id);
        }}
        onPointerOver={(event) => {
          event.stopPropagation();
          onHover(node.id);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          onHover(undefined);
          document.body.style.cursor = 'default';
        }}
      >
        <sphereGeometry args={[1, 24, 24]} />
        <meshStandardMaterial
          color={color}
          emissive={emissive}
          emissiveIntensity={live ? 3.3 : emerging ? 2.15 : selected ? 1.35 : hovered ? 0.85 : 0.14}
          roughness={node.kind === 'folder' ? 0.26 : 0.36}
          metalness={node.kind === 'folder' ? 0.18 : 0.08}
        />
      </mesh>
      {live && <LiveBeacon color={color} scale={scale} pulseOffset={pulseOffset} primary={primaryLive} />}
      {!live && emerging && <BirthBeacon color={color} scale={scale} pulseOffset={pulseOffset} />}
      {!live && selected && <SelectionRing color={color} scale={scale} pulseOffset={pulseOffset} />}
      {showLabel && <NodeEmbeddedLabel node={node} scale={scale} mode={labelMode} color={color} />}
    </group>
  );
}

function NodeField({
  nodes,
  liveNodeIds,
  emergingNodeIds,
  primaryLiveNodeId,
  selectedNodeId,
  hoveredNodeId,
  labelsEnabled,
  navigationMode,
  onSelectNode,
  onHoverNode
}: {
  nodes: PositionedNode[];
  liveNodeIds: Set<string>;
  emergingNodeIds: Set<string>;
  primaryLiveNodeId?: string;
  selectedNodeId?: string;
  hoveredNodeId?: string;
  labelsEnabled: boolean;
  navigationMode: NavigationMode;
  onSelectNode: (nodeId: string) => void;
  onHoverNode: (nodeId?: string) => void;
}) {
  const { camera } = useThree();
  const lastScanAt = useRef(0);
  const scanVector = useRef(new THREE.Vector3());
  const [nearLabelIds, setNearLabelIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!labelsEnabled) setNearLabelIds(new Set());
  }, [labelsEnabled]);

  useFrame(({ clock }) => {
    if (!labelsEnabled) return;
    const elapsed = clock.getElapsedTime();
    const scanInterval = navigationMode === 'fly' ? 0.34 : 0.22;
    if (elapsed - lastScanAt.current < scanInterval) return;
    lastScanAt.current = elapsed;

    const candidates: Array<{ id: string; distance: number }> = [];

    for (const node of nodes) {
      if (node.kind !== 'file' || node.id === selectedNodeId || node.id === hoveredNodeId) continue;
      scanVector.current.set(node.position[0], node.position[1], node.position[2]);
      const distance = camera.position.distanceTo(scanVector.current);
      const threshold = navigationMode === 'fly' ? 16 + node.radius * 3.4 : 20 + node.radius * 4.2;
      if (distance < threshold) candidates.push({ id: node.id, distance });
    }

    const nextIds = candidates
      .sort((a, b) => a.distance - b.distance)
      .slice(0, navigationMode === 'fly' ? 7 : 10)
      .map((entry) => entry.id);

    setNearLabelIds((current) => (sameIdSet(current, nextIds) ? current : new Set(nextIds)));
  });

  return (
    <>
      {nodes.map((node, index) => {
        const selected = selectedNodeId === node.id;
        const hovered = hoveredNodeId === node.id;
        const live = liveNodeIds.has(node.id);
        const emerging = emergingNodeIds.has(node.id);
        const primaryLive = primaryLiveNodeId === node.id;
        const focusLabel = selected || hovered || live || emerging;
        const showLabel = labelsEnabled && (focusLabel || nearLabelIds.has(node.id));

        return (
          <NodeSphere
            key={`${node.id}:${index}`}
            node={node}
            selected={selected}
            hovered={hovered}
            live={live}
            emerging={emerging}
            primaryLive={primaryLive}
            showLabel={showLabel}
            labelMode={focusLabel ? 'focus' : 'near'}
            onSelect={onSelectNode}
            onHover={onHoverNode}
          />
        );
      })}
    </>
  );
}

function NodeEmbeddedLabel({
  node,
  scale,
  mode,
  color
}: {
  node: PositionedNode;
  scale: number;
  mode: 'near' | 'focus';
  color: string;
}) {
  const texture = useMemo(() => createNodeLabelTexture(node.label, node.area, color, mode), [color, mode, node.area, node.label]);
  const labelScale = useMemo(() => {
    const width = THREE.MathUtils.clamp(scale * (mode === 'focus' ? 1.85 : 1.45), 1.05, mode === 'focus' ? 4.8 : 3.2);
    return [width, width * (mode === 'focus' ? 0.36 : 0.26), 1] as [number, number, number];
  }, [mode, scale]);

  useEffect(() => () => texture.dispose(), [texture]);

  return (
    <sprite position={[0, 0, scale * 0.08]} scale={labelScale} renderOrder={45}>
      <spriteMaterial
        map={texture}
        transparent
        depthTest={false}
        depthWrite={false}
        toneMapped={false}
        opacity={mode === 'focus' ? 0.96 : 0.82}
      />
    </sprite>
  );
}

function createNodeLabelTexture(label: string, area: string, color: string, mode: 'near' | 'focus') {
  const canvas = document.createElement('canvas');
  canvas.width = mode === 'focus' ? 512 : 420;
  canvas.height = mode === 'focus' ? 160 : 116;
  const context = canvas.getContext('2d');

  if (!context) {
    return new THREE.CanvasTexture(canvas);
  }

  const width = canvas.width;
  const height = canvas.height;
  context.clearRect(0, 0, width, height);

  const glow = context.createRadialGradient(width / 2, height / 2, 4, width / 2, height / 2, width * 0.42);
  glow.addColorStop(0, `${color}78`);
  glow.addColorStop(0.36, 'rgba(3, 7, 18, 0.42)');
  glow.addColorStop(1, 'rgba(3, 7, 18, 0)');
  context.fillStyle = glow;
  context.fillRect(0, 0, width, height);

  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.shadowColor = 'rgba(0, 0, 0, 0.92)';
  context.shadowBlur = mode === 'focus' ? 10 : 8;
  context.lineJoin = 'round';
  context.strokeStyle = 'rgba(3, 7, 18, 0.84)';
  context.lineWidth = mode === 'focus' ? 8 : 6;
  context.font = mode === 'focus' ? '800 31px Inter, Segoe UI, sans-serif' : '800 24px Inter, Segoe UI, sans-serif';
  context.strokeText(truncateMiddle(label, mode === 'focus' ? 20 : 15), width / 2, mode === 'focus' ? 68 : 58);
  context.fillStyle = '#f8fafc';
  context.fillText(truncateMiddle(label, mode === 'focus' ? 20 : 15), width / 2, mode === 'focus' ? 68 : 58);

  if (mode === 'focus') {
    context.shadowBlur = 7;
    context.strokeStyle = 'rgba(3, 7, 18, 0.72)';
    context.lineWidth = 5;
    context.font = '800 17px Inter, Segoe UI, sans-serif';
    context.strokeText(truncateMiddle(area || 'Vault', 24), width / 2, 112);
    context.fillStyle = color;
    context.fillText(truncateMiddle(area || 'Vault', 24), width / 2, 112);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

function truncateMiddle(value: string, maxLength: number) {
  if (value.length <= maxLength) return value;
  const keep = Math.max(4, Math.floor((maxLength - 1) / 2));
  return `${value.slice(0, keep)}…${value.slice(-keep)}`;
}

function sameIdSet(current: Set<string>, nextIds: string[]) {
  if (current.size !== nextIds.length) return false;
  return nextIds.every((id) => current.has(id));
}

function LiveBeacon({
  color,
  scale,
  pulseOffset,
  primary
}: {
  color: string;
  scale: number;
  pulseOffset: number;
  primary: boolean;
}) {
  return (
    <>
      <mesh scale={[0.026, primary ? 12 + scale * 2 : 7 + scale, 0.026]} position={[0, 0, 0]}>
        <cylinderGeometry args={[1, 1, 1, 12]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={primary ? 0.46 : 0.24}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, pulseOffset]} scale={scale * (primary ? 3.25 : 2.3)}>
        <torusGeometry args={[1, 0.018, 8, 64]} />
        <meshBasicMaterial color={color} transparent opacity={primary ? 0.78 : 0.42} depthWrite={false} />
      </mesh>
      {primary && (
        <mesh rotation={[0, Math.PI / 2, pulseOffset]} scale={scale * 2.7}>
          <torusGeometry args={[1, 0.014, 8, 64]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.38} depthWrite={false} />
        </mesh>
      )}
      <mesh scale={scale * (primary ? 4.8 : 3.2)}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial color={color} transparent opacity={primary ? 0.07 : 0.035} depthWrite={false} />
      </mesh>
    </>
  );
}

function BirthBeacon({ color, scale, pulseOffset }: { color: string; scale: number; pulseOffset: number }) {
  return (
    <>
      <mesh rotation={[Math.PI / 2, 0, pulseOffset]} scale={scale * 2.15}>
        <torusGeometry args={[1, 0.012, 8, 56]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0.42}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
      <mesh scale={scale * 2.8}>
        <sphereGeometry args={[1, 24, 24]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.055}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </>
  );
}

function SelectionRing({
  color,
  scale,
  pulseOffset
}: {
  color: string;
  scale: number;
  pulseOffset: number;
}) {
  return (
    <mesh rotation={[Math.PI / 2, 0, pulseOffset]} scale={scale * 1.95}>
      <torusGeometry args={[1, 0.018, 8, 48]} />
      <meshBasicMaterial color={color} transparent opacity={0.5} depthWrite={false} />
    </mesh>
  );
}

function shouldShowAmbientLink(link: VaultLink, mode: NavigationMode): boolean {
  const base = link.kind === 'property' ? 0.105 : link.kind === 'markdown' ? 0.064 : 0.038;
  const modeFactor = mode === 'fly' ? 0.52 : mode === 'deep' ? 0.62 : mode === 'focus' ? 0.72 : 1;
  const threshold = base * modeFactor;
  return stableHashUnit(link.id) < threshold;
}

function shouldShowFocusLink(
  link: VaultLink,
  selectedNodeId: string | undefined,
  hoveredNodeId: string | undefined,
  mode: NavigationMode
): boolean {
  if (selectedNodeId && (link.source === selectedNodeId || link.target === selectedNodeId)) {
    return stableHashUnit(`selected:${link.id}`) < (mode === 'deep' ? 0.84 : 0.72);
  }

  if (hoveredNodeId && (link.source === hoveredNodeId || link.target === hoveredNodeId)) {
    return stableHashUnit(`hovered:${link.id}`) < (mode === 'fly' ? 0.28 : 0.5);
  }

  const threshold = link.kind === 'property' ? 0.18 : link.kind === 'markdown' ? 0.13 : 0.078;
  return stableHashUnit(`live:${link.id}`) < threshold;
}
